import {
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  EnvironmentInjector,
  Injector,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

import { ComponentType } from '../../core/overlay/generic-component-type';
import {
  NoPortalHostAttachedError,
  NullPortalHostError,
  PortalAlreadyAttachedError,
} from './portal-errors';

export { PortalInjector } from './portal-injector';

/**
 * A `Portal` is something that you want to render somewhere else.
 * It can be attach to / detached from a `PortalHost`.
 */
export abstract class Portal<T> {
  private _attachedHost: PortalHost | null;

  /** Attach this portal to a host. */
  public attach(host: PortalHost): T {
    if (host == null) {
      throw new NullPortalHostError();
    }

    if (host.hasAttached()) {
      throw new PortalAlreadyAttachedError();
    }

    this._attachedHost = host;
    return <T>host.attach(this);
  }

  /** Detach this portal from its host */
  public detach(): void {
    const host = this._attachedHost;
    if (host == null) {
      throw new NoPortalHostAttachedError();
    }

    this._attachedHost = null;
    return host.detach();
  }

  /** Whether this portal is attached to a host. */
  public get isAttached(): boolean {
    return this._attachedHost != null;
  }

  /**
   * Sets the PortalHost reference without performing `attach()`. This is used directly by
   * the PortalHost when it is performing an `attach()` or `detach()`.
   */
  public setAttachedHost(host: PortalHost | null): void {
    this._attachedHost = host;
  }
}

/**
 * A `ComponentPortal` is a portal that instantiates some Component upon attachment.
 */
export class ComponentPortal<T> extends Portal<ComponentRef<T>> {
  constructor(
    /** The type of the component that will be instantiated for attachment. */
    public component: ComponentType<T>,
    /**
     * [Optional] Where the attached component should live in Angular's *logical* component tree.
     * This is different from where the component *renders*, which is determined by the PortalHost.
     * The origin is necessary when the host is outside of the Angular application context.
     */ public viewContainerRef?: ViewContainerRef | null,
    /** [Optional] Injector used for the instantiation of the component. */
    public injector?: Injector,
    public componentFactoryResolver?: ComponentFactoryResolver,
    public environmentInjector?: EnvironmentInjector,
  ) {
    super();
  }
}

/**
 * A `TemplatePortal` is a portal that represents some embedded template (TemplateRef).
 */
export class TemplatePortal extends Portal<Map<string, any>> {
  /** The embedded template that will be used to instantiate an embedded View in the host. */
  public templateRef: TemplateRef<any> | null;

  /** Reference to the ViewContainer into which the template will be stamped out. */
  public viewContainerRef: ViewContainerRef | null;

  /**
   * Additional locals for the instantiated embedded view.
   * These locals can be seen as "exports" for the template, such as how ngFor has
   * index / event / odd.
   * See https://angular.io/docs/ts/latest/api/core/EmbeddedViewRef-class.html
   */
  public locals: Map<string, any> = new Map<string, any>();

  constructor(template: TemplateRef<any> | null, viewContainerRef: ViewContainerRef | null) {
    super();
    this.templateRef = template;
    this.viewContainerRef = viewContainerRef;
  }

  public get origin(): ElementRef {
    return this.templateRef!.elementRef;
  }

  public attach(host: PortalHost, locals?: Map<string, any>): Map<string, any> {
    this.locals = locals == null ? new Map<string, any>() : locals;
    return super.attach(host);
  }

  public detach(): void {
    this.locals = new Map<string, any>();
    return super.detach();
  }
}

/**
 * A `PortalHost` is an space that can contain a single `Portal`.
 */
export interface PortalHost {
  attach: <T>(portal: Portal<T>) => any;

  detach: () => any;

  dispose: () => void;

  hasAttached: () => boolean;
}
